import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { getFilteredStats, sum } from "./helpers";
import { exportSchemeStats } from "./exportSchemeStats";

// ── Empty Section ─────────────────────────────────────────────────────────────
const EmptySection = ({ message = "No data for selected filters" }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
      <svg
        className="w-5 h-5 text-gray-300"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z"
        />
      </svg>
    </div>
    <p className="text-sm font-medium text-gray-400">{message}</p>
    <p className="text-xs text-gray-300">
      Try adjusting or clearing your filters
    </p>
  </div>
);

// ── Stat Card ─────────────────────────────────────────────────────────────────
const NoDataPill = () => (
  <span className="inline-block text-xs font-medium text-gray-400 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-full">
    No data
  </span>
);

const StatCard = ({
  label,
  valueA,
  valueB,
  nameA,
  nameB,
  isSingle,
  emptyA,
  emptyB,
}) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">
      {label}
    </p>
    {isSingle ? (
      emptyA ? (
        <NoDataPill />
      ) : (
        <p className="text-2xl font-black text-gray-800">
          {valueA?.toLocaleString() ?? "—"}
        </p>
      )
    ) : (
      <div className="grid grid-cols-2 gap-2">
        <div
          className={`${emptyA ? "bg-gray-50" : "bg-blue-50"} rounded-lg p-2`}
        >
          <p className="text-xs text-gray-500 font-medium mb-1 truncate">
            {nameA}
          </p>
          {emptyA ? (
            <NoDataPill />
          ) : (
            <p className="text-lg font-black text-blue-700">
              {valueA?.toLocaleString() ?? "—"}
            </p>
          )}
        </div>
        <div
          className={`${emptyB ? "bg-gray-50" : "bg-emerald-50"} rounded-lg p-2`}
        >
          <p className="text-xs text-gray-500 font-medium mb-1 truncate">
            {nameB}
          </p>
          {emptyB ? (
            <NoDataPill />
          ) : (
            <p className="text-lg font-black text-emerald-700">
              {valueB?.toLocaleString() ?? "—"}
            </p>
          )}
        </div>
      </div>
    )}
  </div>
);

// ── Chart ─────────────────────────────────────────────────────────────────────
const COLORS = {
  A: { primary: "#3b82f6", secondary: "#93c5fd" },
  B: { primary: "#10b981", secondary: "#6ee7b7" },
};

// Palette for pie slices
const PIE_PALETTE = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#f97316",
  "#84cc16",
  "#ec4899",
  "#6366f1",
];

// Tabs that support pie chart
const PIE_SUPPORTED_TABS = ["categories", "points_range"];

// ── Custom Pie Label ──────────────────────────────────────────────────────────
const renderPieLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  name,
}) => {
  if (percent < 0.04) return null; // skip tiny slices
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      style={{ fontSize: 11, fontWeight: 700 }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// ── Custom Tooltip for Pie ────────────────────────────────────────────────────
const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { name, value } = payload[0];
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-700">{name}</p>
      <p className="text-gray-500">
        {Number(Number(value).toFixed(2)).toLocaleString()}
      </p>
    </div>
  );
};

// ── Single Donut ──────────────────────────────────────────────────────────────
const DonutChart = ({ data, title, color }) => {
  if (!data?.length)
    return (
      <div className="flex items-center justify-center h-48 text-gray-300 text-xs">
        No data
      </div>
    );
  return (
    <div className="flex flex-col items-center">
      {title && (
        <span
          className="text-xs font-bold mb-2 px-3 py-1 rounded-full text-white"
          style={{ backgroundColor: color }}
        >
          {title}
        </span>
      )}
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={3}
            dataKey="value"
            labelLine={false}
            label={renderPieLabel}
            animationBegin={0}
            animationDuration={600}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />
            ))}
          </Pie>
          <Tooltip content={<PieTooltip />} />
          <Legend
            iconType="circle"
            iconSize={8}
            formatter={(value) => (
              <span style={{ fontSize: 11, color: "#6b7280" }}>{value}</span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

// ── Pie View (single or side-by-side donuts) ──────────────────────────────────
const PieView = ({ tab, sA, sB, nameA, nameB, isSingle }) => {
  const buildPieData = (source) => {
    if (tab === "categories") {
      return (source?.product_categories ?? []).map((r) => ({
        name: r.category_name,
        value: r.total_scans,
      }));
    }
    if (tab === "points_range") {
      return (source?.points_range_distribution ?? []).map((r) => ({
        name: r.range,
        value: r.count,
      }));
    }
    return [];
  };

  const dataA = buildPieData(sA);
  const dataB = buildPieData(sB);

  if (isSingle) {
    return <DonutChart data={dataA} />;
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <DonutChart data={dataA} title={nameA} color={COLORS.A.primary} />
      <DonutChart data={dataB} title={nameB} color={COLORS.B.primary} />
    </div>
  );
};

// ── Bar View ──────────────────────────────────────────────────────────────────
const BarView = ({ tab, sA, sB, nameA, nameB, isSingle }) => {
  const buildData = () => {
    if (tab === "states") {
      const map = {};
      (sA?.states ?? []).forEach((r) => {
        map[r.state] = {
          name: r.state,
          a_earned: r.total_points_earned,
          a_participants: r.total_participants,
        };
      });
      (sB?.states ?? []).forEach((r) => {
        map[r.state] = map[r.state] ?? { name: r.state };
        map[r.state].b_earned = r.total_points_earned;
        map[r.state].b_participants = r.total_participants;
      });
      return Object.values(map);
    }
    if (tab === "products") {
      const map = {};
      (sA?.product_codes ?? []).forEach((r) => {
        map[r.product_code] = {
          name: r.product_code,
          a_scans: r.total_scans,
          a_earned: r.total_points_earned,
        };
      });
      (sB?.product_codes ?? []).forEach((r) => {
        map[r.product_code] = map[r.product_code] ?? { name: r.product_code };
        map[r.product_code].b_scans = r.total_scans;
        map[r.product_code].b_earned = r.total_points_earned;
      });
      return Object.values(map);
    }
    if (tab === "categories") {
      const map = {};
      (sA?.product_categories ?? []).forEach((r) => {
        map[r.category_name] = {
          name: r.category_name,
          a_scans: r.total_scans,
          a_earned: r.total_points_earned,
        };
      });
      (sB?.product_categories ?? []).forEach((r) => {
        map[r.category_name] = map[r.category_name] ?? {
          name: r.category_name,
        };
        map[r.category_name].b_scans = r.total_scans;
        map[r.category_name].b_earned = r.total_points_earned;
      });
      return Object.values(map);
    }
    if (tab === "points_range") {
      const map = {};
      (sA?.points_range_distribution ?? []).forEach((r) => {
        map[r.range] = { name: r.range, a_count: r.count };
      });
      (sB?.points_range_distribution ?? []).forEach((r) => {
        map[r.range] = map[r.range] ?? { name: r.range };
        map[r.range].b_count = r.count;
      });
      return Object.values(map);
    }
    return [];
  };

  const barConfigs = {
    states: isSingle
      ? [
          { key: "a_earned", label: "Points Earned", color: COLORS.A.primary },
          {
            key: "a_participants",
            label: "Participants",
            color: COLORS.A.secondary,
          },
        ]
      : [
          {
            key: "a_earned",
            label: `${nameA} Earned`,
            color: COLORS.A.primary,
          },
          {
            key: "b_earned",
            label: `${nameB} Earned`,
            color: COLORS.B.primary,
          },
          {
            key: "a_participants",
            label: `${nameA} Participants`,
            color: COLORS.A.secondary,
          },
          {
            key: "b_participants",
            label: `${nameB} Participants`,
            color: COLORS.B.secondary,
          },
        ],
    products: isSingle
      ? [
          { key: "a_scans", label: "Scans", color: COLORS.A.primary },
          { key: "a_earned", label: "Earned", color: COLORS.A.secondary },
        ]
      : [
          { key: "a_scans", label: `${nameA} Scans`, color: COLORS.A.primary },
          { key: "b_scans", label: `${nameB} Scans`, color: COLORS.B.primary },
          {
            key: "a_earned",
            label: `${nameA} Earned`,
            color: COLORS.A.secondary,
          },
          {
            key: "b_earned",
            label: `${nameB} Earned`,
            color: COLORS.B.secondary,
          },
        ],
    categories: isSingle
      ? [
          { key: "a_scans", label: "Scans", color: COLORS.A.primary },
          { key: "a_earned", label: "Earned", color: COLORS.A.secondary },
        ]
      : [
          { key: "a_scans", label: `${nameA} Scans`, color: COLORS.A.primary },
          { key: "b_scans", label: `${nameB} Scans`, color: COLORS.B.primary },
        ],
    points_range: isSingle
      ? [{ key: "a_count", label: "Users", color: COLORS.A.primary }]
      : [
          { key: "a_count", label: `${nameA} Users`, color: COLORS.A.primary },
          { key: "b_count", label: `${nameB} Users`, color: COLORS.B.primary },
        ],
  };

  const data = buildData();
  const activeBars = barConfigs[tab] ?? [];

  if (data.length === 0) return null; // handled by parent

  return (
    <div>
      <ResponsiveContainer width="100%" height={380}>
        <BarChart
          data={data}
          margin={{ top: 10, right: 20, left: 10, bottom: 70 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#f0f0f0"
            vertical={false}
          />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: "#9ca3af" }}
            angle={-35}
            textAnchor="end"
            interval={0}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "#9ca3af" }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              fontSize: "12px",
            }}
            cursor={{ fill: "rgba(0,0,0,0.03)" }}
            formatter={(value) => [
              Number(Number(value).toFixed(2)).toLocaleString(),
              undefined,
            ]}
          />
          {activeBars.map((b) => (
            <Bar
              key={b.key}
              dataKey={b.key}
              name={b.label}
              fill={b.color}
              radius={[4, 4, 0, 0]}
              maxBarSize={40}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center pt-2 pb-1 border-t border-gray-100 mt-2">
        {activeBars.map((b) => (
          <span
            key={b.key}
            className="flex items-center gap-1.5 text-xs text-gray-600"
          >
            <span
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: b.color }}
            />
            {b.label}
          </span>
        ))}
      </div>
    </div>
  );
};

// ── Chart Type Toggle ─────────────────────────────────────────────────────────
const ChartToggle = ({ value, onChange }) => (
  <div className="flex items-center bg-gray-100 rounded-lg p-0.5 gap-0.5">
    <button
      onClick={() => onChange("bar")}
      title="Bar chart"
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
        value === "bar"
          ? "bg-white text-blue-600 shadow-sm"
          : "text-gray-500 hover:text-gray-700"
      }`}
    >
      {/* bar icon */}
      <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor">
        <rect x="1" y="8" width="3" height="7" rx="1" />
        <rect x="6" y="4" width="3" height="11" rx="1" />
        <rect x="11" y="1" width="3" height="14" rx="1" />
      </svg>
      Bar
    </button>
    <button
      onClick={() => onChange("pie")}
      title="Pie chart"
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
        value === "pie"
          ? "bg-white text-blue-600 shadow-sm"
          : "text-gray-500 hover:text-gray-700"
      }`}
    >
      {/* pie icon */}
      <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 2v5h5a5 5 0 01-5 5V8H3a5 5 0 015-5z" />
      </svg>
      Pie
    </button>
  </div>
);

// ── Main SchemeChart (orchestrator) ───────────────────────────────────────────
const SchemeChart = ({
  tab,
  sA,
  sB,
  nameA,
  nameB,
  isSingle,
  chartType,
  onChartTypeChange,
}) => {
  const pieSupported = PIE_SUPPORTED_TABS.includes(tab);

  // Check emptiness
  const getTabData = () => {
    if (tab === "states") return [...(sA?.states ?? []), ...(sB?.states ?? [])];
    if (tab === "products")
      return [...(sA?.product_codes ?? []), ...(sB?.product_codes ?? [])];
    if (tab === "categories")
      return [
        ...(sA?.product_categories ?? []),
        ...(sB?.product_categories ?? []),
      ];
    if (tab === "points_range")
      return [
        ...(sA?.points_range_distribution ?? []),
        ...(sB?.points_range_distribution ?? []),
      ];
    return [];
  };

  if (getTabData().length === 0)
    return (
      <EmptySection
        message={
          tab === "products"
            ? "No product code data for selected filters"
            : tab === "categories"
              ? "No category data for selected filters"
              : tab === "states"
                ? "No state data for selected filters"
                : "No data for selected filters"
        }
      />
    );

  const activeType = pieSupported ? chartType : "bar";

  return (
    <div>
      {/* Toggle row — only shown for pie-supported tabs */}
      {pieSupported && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs text-gray-400 font-medium">
            {tab === "categories"
              ? "Category distribution"
              : "Points range distribution"}
          </p>
          <ChartToggle value={activeType} onChange={onChartTypeChange} />
        </div>
      )}

      {activeType === "pie" ? (
        <PieView
          tab={tab}
          sA={sA}
          sB={sB}
          nameA={nameA}
          nameB={nameB}
          isSingle={isSingle}
        />
      ) : (
        <BarView
          tab={tab}
          sA={sA}
          sB={sB}
          nameA={nameA}
          nameB={nameB}
          isSingle={isSingle}
        />
      )}
    </div>
  );
};

// ── Main Modal ────────────────────────────────────────────────────────────────
const StatsModal = ({
  open,
  onClose,
  statsA,
  statsB,
  nameA,
  nameB,
  winner = null,
}) => {
  const [activeChart, setActiveChart] = useState("states");
  const [chartType, setChartType] = useState("bar");

  if (!open) return null;

  const sA = getFilteredStats(statsA);
  const sB = statsB ? getFilteredStats(statsB) : null;
  const isSingle = !sB;

  const chartTabs = [
    { key: "states", label: "By State" },
    { key: "products", label: "By Product" },
    { key: "categories", label: "By Category" },
    { key: "points_range", label: "Points Range" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-gray-50 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-800">
              {isSingle
                ? `Stats Overview — ${nameA}`
                : `${nameA}  vs  ${nameB}`}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {isSingle
                ? "Summary cards and charts"
                : "Side-by-side overview and charts"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Export to Excel */}
            <button
              onClick={() =>
                exportSchemeStats(statsA, statsB, nameA, nameB, winner)
              }
              className="flex items-center gap-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 px-3 py-2 rounded-xl transition-colors"
              title="Export to Excel"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Export
            </button>

            {/* Close */}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {/* Scheme badges for 2-scheme mode */}
          {!isSingle && (
            <div className="flex gap-3">
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-full">
                <span className="w-2 h-2 rounded-full bg-blue-500" /> {nameA}
              </span>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full">
                <span className="w-2 h-2 rounded-full bg-emerald-500" /> {nameB}
              </span>
            </div>
          )}

          {/* Overview cards */}
          <section>
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">
              Overview
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard
                label="Total Participants"
                isSingle={isSingle}
                nameA={nameA}
                nameB={nameB}
                valueA={sum(sA?.states, "total_participants")}
                valueB={sB ? sum(sB?.states, "total_participants") : null}
                emptyA={!sA?.states?.length}
                emptyB={!sB?.states?.length}
              />
              <StatCard
                label="Points Earned"
                isSingle={isSingle}
                nameA={nameA}
                nameB={nameB}
                valueA={Number(
                  sum(sA?.states, "total_points_earned").toFixed(2),
                )}
                valueB={
                  sB
                    ? Number(sum(sB?.states, "total_points_earned").toFixed(2))
                    : null
                }
                emptyA={!sA?.states?.length}
                emptyB={!sB?.states?.length}
              />
              <StatCard
                label="Points Redeemed"
                isSingle={isSingle}
                nameA={nameA}
                nameB={nameB}
                valueA={Number(
                  sum(sA?.states, "total_points_redeemed").toFixed(2),
                )}
                valueB={
                  sB
                    ? Number(
                        sum(sB?.states, "total_points_redeemed").toFixed(2),
                      )
                    : null
                }
                emptyA={!sA?.states?.length}
                emptyB={!sB?.states?.length}
              />
              <StatCard
                label="Total Product Scans"
                isSingle={isSingle}
                nameA={nameA}
                nameB={nameB}
                valueA={sum(sA?.product_codes, "total_scans")}
                valueB={sB ? sum(sB?.product_codes, "total_scans") : null}
                emptyA={!sA?.product_codes?.length}
                emptyB={!sB?.product_codes?.length}
              />
              <StatCard
                label="Unique Users"
                isSingle={isSingle}
                nameA={nameA}
                nameB={nameB}
                valueA={sum(sA?.product_codes, "unique_users")}
                valueB={sB ? sum(sB?.product_codes, "unique_users") : null}
                emptyA={!sA?.product_codes?.length}
                emptyB={!sB?.product_codes?.length}
              />
              <StatCard
                label="Category Scans"
                isSingle={isSingle}
                nameA={nameA}
                nameB={nameB}
                valueA={sum(sA?.product_categories, "total_scans")}
                valueB={sB ? sum(sB?.product_categories, "total_scans") : null}
                emptyA={!sA?.product_categories?.length}
                emptyB={!sB?.product_categories?.length}
              />
            </div>
          </section>

          {/* Charts */}
          <section>
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">
              Charts
            </p>

            {/* Chart sub-tabs */}
            <div className="flex gap-2 mb-5">
              {chartTabs.map((ct) => (
                <button
                  key={ct.key}
                  onClick={() => {
                    setActiveChart(ct.key);
                    setChartType("bar");
                  }}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                    activeChart === ct.key
                      ? "bg-blue-600 text-white shadow-sm"
                      : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {ct.label}
                </button>
              ))}
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <SchemeChart
                tab={activeChart}
                sA={sA}
                sB={sB}
                nameA={nameA}
                nameB={nameB}
                isSingle={isSingle}
                chartType={chartType}
                onChartTypeChange={setChartType}
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default StatsModal;

